import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  ArrowLeft, Ship, FileText, ShieldCheck, PoundSterling, MessageSquare,
  Loader2, Download, Calendar, MapPin, CheckCircle2, XCircle, Calculator, FileCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountantClientView = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trips');
  const [trips, setTrips] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [eligibility, setEligibility] = useState(null);

  useEffect(() => {
    fetchClientData();
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClientData = async () => {
    setLoading(true);
    try {
      const [tripsRes, docsRes, eligRes] = await Promise.all([
        axios.get(`${API_URL}/api/accountant/clients/${clientId}/trips`),
        axios.get(`${API_URL}/api/accountant/clients/${clientId}/documents`),
        axios.get(`${API_URL}/api/accountant/clients/${clientId}/eligibility`)
      ]);
      setTrips(tripsRes.data.trips || []);
      setDocuments(docsRes.data.documents || []);
      setEligibility(eligRes.data);
    } catch (error) {
      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

  const tabs = [
    { key: 'trips', label: 'Trips', icon: Ship, count: trips.length },
    { key: 'documents', label: 'Documents', icon: FileText, count: documents.length },
    { key: 'eligibility', label: 'SED Status', icon: ShieldCheck },
    { key: 'messages', label: 'Messages', icon: MessageSquare },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-view">
      {/* Back + Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/accountant/dashboard')}
            className="text-slate-400 hover:text-white h-8 w-8" data-testid="back-to-dashboard">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white">Client Details</h1>
            <p className="text-xs text-slate-500">Read-only view of SED-relevant data</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => navigate(`/accountant/doc-checklist/${clientId}`)}
            className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid="open-doc-checklist">
            <FileCheck className="h-3.5 w-3.5 mr-1.5" />
            Checklist
          </Button>
          <Button size="sm" onClick={() => navigate(`/accountant/tax-workspace/${clientId}`)}
            className="bg-sky-600 hover:bg-sky-700 h-9" data-testid="open-tax-workspace">
            <Calculator className="h-3.5 w-3.5 mr-1.5" />
            Tax Workspace
          </Button>
          <Button size="sm" onClick={() => navigate(`/accountant/tax-return/${clientId}`)}
            className="bg-amber-600 hover:bg-amber-700 h-9" data-testid="open-tax-return">
            <Calculator className="h-3.5 w-3.5 mr-1.5" />
            SA100
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-800 p-1 rounded-lg overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.key ? 'bg-sky-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700'
            }`} data-testid={`tab-${tab.key}`}>
            <tab.icon className="h-3.5 w-3.5" />
            {tab.label}
            {tab.count !== undefined && <span className="bg-slate-700/50 px-1.5 py-0.5 rounded-full text-[10px]">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'trips' && (
        <div className="space-y-2">
          {trips.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6 text-center text-slate-500 text-sm">No trips recorded yet.</CardContent>
            </Card>
          ) : (
            trips.sort((a, b) => (b.date_left_uk || '').localeCompare(a.date_left_uk || '')).map(trip => (
              <Card key={trip.id} className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white">{trip.trip_name || trip.vessel_name || 'Trip'}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          trip.trip_type === 'work' ? 'bg-sky-500/15 text-sky-400' : 'bg-amber-500/15 text-amber-400'
                        }`}>{trip.trip_type}</span>
                        {trip.qualifying_days > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">
                            {trip.qualifying_days}d qualifying
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />{formatDate(trip.date_left_uk)} — {formatDate(trip.date_returned_uk)}
                        </span>
                        {trip.vessel_name && <span className="flex items-center gap-1"><Ship className="h-3 w-3" />{trip.vessel_name}</span>}
                      </div>
                    </div>
                    {trip.total_earnings > 0 && (
                      <div className="text-right shrink-0">
                        <p className="text-sm font-medium text-amber-400">£{trip.total_earnings?.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-2">
          {documents.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6 text-center text-slate-500 text-sm">No documents uploaded yet.</CardContent>
            </Card>
          ) : (
            documents.map(doc => (
              <Card key={doc.id} className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{doc.file_name || doc.original_file_name || 'Document'}</p>
                        <p className="text-xs text-slate-500">{formatDate(doc.uploaded_at || doc.created_at)}</p>
                      </div>
                    </div>
                    {doc.document_type && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700 text-slate-400">{doc.document_type}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {activeTab === 'eligibility' && eligibility && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-sky-400" />
              SED Eligibility Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-700/40 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Status</p>
                <div className="flex items-center gap-2">
                  {eligibility.sed_eligible ? (
                    <><CheckCircle2 className="h-4 w-4 text-emerald-400" /><span className="text-emerald-400 font-medium text-sm">Eligible</span></>
                  ) : (
                    <><XCircle className="h-4 w-4 text-slate-500" /><span className="text-slate-400 font-medium text-sm">Not Yet Eligible</span></>
                  )}
                </div>
              </div>
              <div className="p-3 bg-slate-700/40 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Qualifying Days</p>
                <p className="text-xl font-bold text-white">{eligibility.qualifying_days || 0}</p>
              </div>
              <div className="p-3 bg-slate-700/40 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Days in Hand</p>
                <p className="text-xl font-bold text-white">{eligibility.days_in_hand || 0}</p>
              </div>
              <div className="p-3 bg-slate-700/40 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Total Earnings</p>
                <p className="text-xl font-bold text-amber-400">£{(eligibility.total_earnings || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'messages' && (
        <ClientMessages clientId={clientId} />
      )}
    </div>
  );
};

// Inline messages component
const ClientMessages = ({ clientId }) => {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accountant/messages/${clientId}`);
      setMessages(res.data.messages || []);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/accountant/messages`, {
        content: newMsg.trim(),
        client_user_id: clientId
      });
      setNewMsg('');
      fetchMessages();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardContent className="p-4">
        <div className="h-[400px] flex flex-col">
          <div className="flex-1 overflow-y-auto space-y-3 mb-3 pr-1">
            {loading ? (
              <div className="flex items-center justify-center h-full"><Loader2 className="h-5 w-5 animate-spin text-sky-400" /></div>
            ) : messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-slate-500 text-sm">No messages yet.</div>
            ) : (
              messages.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender_role === 'accountant' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-lg px-3 py-2 ${
                    msg.sender_role === 'accountant' ? 'bg-sky-600/20 text-sky-100' : 'bg-slate-700 text-slate-200'
                  }`}>
                    <p className="text-sm">{msg.content}</p>
                    <p className="text-[10px] text-slate-500 mt-1">{new Date(msg.created_at).toLocaleString('en-GB')}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex gap-2 pt-3 border-t border-slate-700">
            <input
              value={newMsg} onChange={(e) => setNewMsg(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder="Type a message..."
              className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500"
              data-testid="message-input"
            />
            <Button onClick={sendMessage} disabled={sending || !newMsg.trim()}
              className="bg-sky-600 hover:bg-sky-700 h-9 px-4" data-testid="send-message-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AccountantClientView;
